<template>
  <div v-if="lines.length" class="rt" role="region" aria-label="Recent achievements">
    <span class="rt-label">🎉 Just happened</span>
    <div ref="viewport" class="rt-viewport" :class="{ faded: moving }">
      <div ref="track" class="rt-track" :class="{ moving }" :style="moving ? { animationDuration: duration + 's' } : null">
        <a v-for="(l, i) in shown" :key="i" class="rt-item" :href="l.href"
           :tabindex="i >= lines.length ? -1 : null" :aria-hidden="i >= lines.length ? 'true' : null">
          <span class="rt-emoji">{{ l.emoji }}</span> <b>{{ l.who }}</b> {{ l.what }} <span class="rt-ago">{{ l.ago }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// The bracket page's "just happened" strip: the last week's title milestones, quest
// completions, long reigns and more, newest first, from recentEvents.json (built on the
// refresh box after every tournament; see tools/buildRecentEvents.mjs). It re-reads the
// newest manifest every 10 minutes, so an open tab picks up new events. The newest Daily
// Chocobo leads it while it's fresh (lib/chocoboLatest.js; it covers yesterday's chat).
//
// It scrolls only when the lines don't fit, pauses under the mouse or keyboard focus,
// and holds still (scrollable by hand) for anyone with reduced motion turned on.
// Settings has a switch to hide it (prefs.showTicker).
import { latestManifest, loadFile } from '../lib/dataStore.js';
import { eventLine, ago } from '../lib/recentEvents.js';
import { latestIssue, issueWhen, issueHref } from '../lib/chocoboLatest.js';

const REFRESH_MS = 10 * 60 * 1000;
const SPEED = 45;   // px per second

export default {
  name: 'RecentTicker',
  data() {
    return { events: [], key: '', now: Date.now(), moving: false, duration: 60, timer: null, paper: null };
  },
  computed: {
    lines() {
      const out = this.events.map((e) => { const l = eventLine(e); return l && { ...l, ago: ago(e.t, this.now) }; }).filter(Boolean);
      // the newest Daily Chocobo leads while it's fresh (yesterday's or today's chat)
      const p = this.paper, when = p && issueWhen(p.issue.date, new Date(this.now));
      if (p && (when === 'Yesterday' || when === 'Today')) out.unshift({ emoji: '📰', who: p.paper.name + ':', what: p.issue.headline, href: issueHref(p.issue.date), ago: when.toLowerCase() + '’s chat' });
      return out;
    },
    // Two copies while moving: the track slides left by exactly one copy, then repeats.
    shown() { return this.moving ? [...this.lines, ...this.lines] : this.lines; },
  },
  created() {
    this.refresh();
    setTimeout(() => latestIssue().then((p) => { this.paper = p; this.$nextTick(this.measure); }), 2500);   // after the page, like the front-page card
    this.timer = setInterval(this.refresh, REFRESH_MS);
  },
  mounted() {
    this.onResize = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(this.measure, 200); };
    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    async refresh() {
      this.now = Date.now();
      try {
        const m = await latestManifest();
        const key = m && m.files && m.files['recentEvents.json'];
        if (!key || key === this.key) return;
        const data = await loadFile(key);
        this.key = key;
        this.events = (data && data.events) || [];
        this.measure();
      } catch { /* no ticker this time; the next refresh tries again */ }
    },
    // Scroll only if one copy of the lines is wider than the space for them.
    async measure() {
      this.moving = false;
      await this.$nextTick();
      const track = this.$refs.track, viewport = this.$refs.viewport;
      if (!track || !viewport) return;
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const width = track.scrollWidth;
      if (reduced || width <= viewport.clientWidth) return;
      this.duration = Math.round(width / SPEED);
      this.moving = true;
    },
  },
};
</script>

<style scoped>
.rt {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  padding: 5px 12px;
  background: linear-gradient(180deg, var(--panel-top), var(--navy2));
  border: 1px solid var(--brass-edge);
  border-radius: 4px;
  color: var(--ink);
  font-size: 13px;
  line-height: 1.5;
}
.rt-label { flex: 0 0 auto; font-family: var(--cond); font-weight: 700; font-size: 14px; letter-spacing: 1.6px; text-transform: uppercase; color: var(--brass); white-space: nowrap; }
.rt-viewport { flex: 1 1 auto; min-width: 0; overflow: hidden; }
/* Soft edges while it scrolls, so lines slide in and out rather than getting cut. */
.rt-viewport.faded {
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 20px, #000 calc(100% - 20px), transparent);
  mask-image: linear-gradient(90deg, transparent, #000 20px, #000 calc(100% - 20px), transparent);
}
.rt-track { display: inline-flex; white-space: nowrap; }
/* A margin after every item (not a flex gap) keeps the two copies exactly one copy apart. */
.rt-item { margin-right: 32px; color: inherit; text-decoration: none; }
.rt-item b { color: var(--ink-hi); }
.rt-item:hover b, .rt-item:focus b { text-decoration: underline; color: var(--brass-hi); }
.rt-emoji { margin-right: 2px; }
.rt-ago { color: var(--mute); font-size: 11.5px; margin-left: 2px; }
.rt-track.moving { animation: rt-scroll linear infinite; }
.rt:hover .rt-track, .rt:focus-within .rt-track { animation-play-state: paused; }
@keyframes rt-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@media (prefers-reduced-motion: reduce) {
  .rt-track.moving { animation: none; }
  .rt-viewport { overflow-x: auto; }
}
@media (max-width: 600px) {
  .rt-label { display: none; }
}
</style>
